import { Form, Field, FieldProps } from "formik"
import { ChangeEvent, FC } from "react"
import CurrencyFormat from "react-currency-format"
import { SelectValue, FormSelect } from "../FormSelect"
import { InputField } from "./InputField"

export type InputFormProps = {
  /**
   * Form errors
   */
  errors: any
  /**
   * Field of values which indicates if a field was interacted with
   */
  touched: any
  /**
   * Form handle change function
   */
  handleChange: (
    event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: { id: number; value: string } } }
  ) => void

  /**
   * Field handle set field value
   */
  setFieldValue: any
}

const validateNumber = (n: any) => {
  let error

  if (isNaN(parseFloat(n)) && !isNaN(n - 0)) {
    error = "Please enter a number."
  }

  return error
}

export const InputForm: FC<InputFormProps> = ({ errors, touched, handleChange, setFieldValue }) => {
  const stageRegularValues = [
    { id: 1, value: 1 },
    { id: 2, value: 2 },
    { id: 3, value: 3 },
  ]

  const segmentValues = [
    { id: 1, value: 1 },
    { id: 2, value: 2 },
    { id: 3, value: 3 },
    { id: 4, value: 4 },
    { id: 5, value: 5 },
  ]

  const paymentFreqValues = [
    { id: 1, value: 1 },
    { id: 2, value: 2 },
    { id: 3, value: 4 },
    { id: 4, value: 12 },
  ]

  const obligorRatingValues = [
    { id: 1, value: "A" },
    { id: 2, value: "B" },
    { id: 4, value: "C" },
    { id: 12, value: "D" },
  ]

  const cashFlowTypeValues = [
    { id: 1, value: "IO" },
    { id: 2, value: "IO w/ Level" },
    { id: 3, value: "IO w/ Balloon" },
    { id: 4, value: "Level" },
    { id: 5, value: "Balloon" },
    { id: 6, value: "Straight Line w/ Life" },
    { id: 7, value: "Straight Line w/ Ratio" },
    { id: 8, value: "Pay at Maturity" },
    { id: 9, value: "Fixed Life" },
  ]

  return (
    <Form className="flex flex-col items-center justify-center pb-6 space-y-3">
      <Field name="Segment">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <FormSelect
              label={"Segment"}
              values={segmentValues}
              selected={field.value}
              setSelected={(e: SelectValue) => handleChange({ target: { name: field.name, value: e } })}
            />
          </div>
        )}
      </Field>

      <Field name="ObligorRating">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <FormSelect
              label={"Obligor Rating (Internal Rating)"}
              values={obligorRatingValues}
              selected={field.value}
              setSelected={(e: SelectValue) => handleChange({ target: { name: field.name, value: e } })}
            />
          </div>
        )}
      </Field>

      <Field name="LGD" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <label htmlFor={"LGD"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
              {"Loss Given Default (LGD)"}
            </label>
            <div className="mt-1">
              <CurrencyFormat
                suffix={"%"}
                id="LGD"
                name="LGD"
                value={parseFloat(field.value)}
                displayType={"input"}
                onValueChange={(values) => {
                  setFieldValue("LGD", parseFloat(values.value))
                }}
                error={errors.LGD}
                className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
                max={100}
                allowNegative={false}
              />
            </div>
          </div>
        )}
      </Field>

      <Field name="RecoveryLags" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <InputField
            id="RecoveryLags"
            label="Recovery Lag (Months)"
            type="number"
            value={field.value}
            handleChange={handleChange}
            error={errors.RecoveryLags}
            touched={touched.RecoveryLags}
          />
        )}
      </Field>

      <Field name="Stage_Regular">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <FormSelect
              label={"Current Stage"}
              values={stageRegularValues}
              selected={field.value}
              setSelected={(e: SelectValue) => handleChange({ target: { name: field.name, value: e } })}
            />
          </div>
        )}
      </Field>

      <Field name="CashFlowTypes">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <FormSelect
              label={"Cash Flow Types"}
              values={cashFlowTypeValues}
              selected={field.value}
              setSelected={(e: SelectValue) => handleChange({ target: { name: field.name, value: e } })}
            />
          </div>
        )}
      </Field>

      <Field name="PaymentFreq" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <FormSelect
              label={"Payment Frequency"}
              values={paymentFreqValues}
              selected={field.value}
              setSelected={(e: SelectValue) => handleChange({ target: { name: field.name, value: e } })}
            />
          </div>
        )}
      </Field>

      <Field name="AssessmentDate">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              id="AssessmentDate"
              label="Assessment Date"
              type="date"
              value={field.value}
              handleChange={handleChange}
            />
          </div>
        )}
      </Field>

      <Field name="NextPaymentDate">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              id="NextPaymentDate"
              label="Next Payment Date"
              type="date"
              value={field.value}
              handleChange={handleChange}
            />
          </div>
        )}
      </Field>

      <Field name="OutstandingBalance" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <label
              htmlFor={"OutstandingBalance"}
              className="block text-sm px-3 font-medium text-semibold text-gray-700"
            >
              {"Outstanding Balance"}
            </label>
            <div className="mt-1">
              <CurrencyFormat
                thousandSeparator={true}
                prefix={"$"}
                id="OutstandingBalance"
                name="OutstandingBalance"
                value={field.value}
                displayType={"input"}
                onValueChange={(values) => {
                  setFieldValue("OutstandingBalance", values.value)
                }}
                error={errors.OutstandingBalance}
                className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
              />
            </div>
          </div>
        )}
      </Field>

      <Field name="LoanLimit" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <label htmlFor={"LoanLimit"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
              {"Loan Limit"}
            </label>
            <div className="mt-1">
              <CurrencyFormat
                thousandSeparator={true}
                prefix={"$"}
                id="LoanLimit"
                name="LoanLimit"
                value={field.value}
                displayType={"input"}
                onValueChange={(values) => {
                  setFieldValue("LoanLimit", values.value)
                }}
                error={errors.LoanLimit}
                className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
              />
            </div>
          </div>
        )}
      </Field>

      <Field name="CCF" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <label htmlFor={"CCF"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
              {"Credit Conversion Factor"}
            </label>
            <div className="mt-1">
              <CurrencyFormat
                thousandSeparator={true}
                suffix={"%"}
                id="CCF"
                name="Credit Conversion Factor"
                value={field.value}
                displayType={"input"}
                onValueChange={(values) => {
                  setFieldValue("CCF", values.value)
                }}
                error={errors.CCF}
                className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
              />
            </div>
          </div>
        )}
      </Field>

      <Field name="Contract_IR" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <label htmlFor={"Contract_IR"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
              {"Contractual Interest Rate (Annual)"}
            </label>
            <div className="mt-1">
              <CurrencyFormat
                thousandSeparator={true}
                suffix={"%"}
                id="Contract_IR"
                name="Contract_IR"
                value={field.value}
                displayType={"input"}
                onValueChange={(values) => {
                  setFieldValue("Contract_IR", values.value)
                }}
                error={errors.CCF}
                className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
              />
            </div>
          </div>
        )}
      </Field>

      <button className="bg-brand-light rounded text-white py-2 px-8 mt-3 text-sm" type="submit">
        CALCULATE
      </button>
    </Form>
  )
}

